import React, { useEffect, useState } from "react";
import {
  Inventory2Outlined,
  SaveOutlined,
  RestartAltOutlined,
  ExpandMoreOutlined,
  EditOutlined,
  DeleteOutlineOutlined,
  CloseOutlined,
} from "@mui/icons-material";

import AddProduct from "../components/Products/AddProduct";
import ProductDetails from "../components/Products/ProductDetails";
import ProductList from "../components/Products/ProductList";
import { BASE_URL } from "../constants";

const Products = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [products, setProducts] = useState([]);

  const [loadingProducts, setLoadingProducts] = useState(false);

  const [productError, setProductError] = useState("");

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });

  /*
  |--------------------------------------------------------------------------
  | GET ALL PRODUCTS
  |--------------------------------------------------------------------------
  */

  const getAllProducts = async (
    page = 1,
    limit = pagination.limit,
    search = "",
  ) => {
    try {
      setLoadingProducts(true);
      setProductError("");

      const token = localStorage.getItem("accessToken");

      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (search.trim()) {
        queryParams.append("search", search.trim());
      }

      const response = await fetch(
        `${BASE_URL}api/user/products?${queryParams.toString()}`,
        {
          method: "GET",

          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch products");
      }

      /*
       * Backend response:
       *
       * {
       *   success: true,
       *   data: [...],
       *   pagination: {...}
       * }
       */

      const fetchedProducts = (data.data || []).map((product) => ({
        ...product,

        // Keep frontend naming consistent
        productName: product.name,

        // Convert backend boolean to UI value
        decimal: product.allowDecimal ? "Yes" : "No",

        // Backend may contain unitConversion
        conversionQty: product.unitConversion ?? product.conversionQty ?? 1,

        // Keep tests ready for BOM later
        tests: product.tests || [],
      }));

      setProducts(fetchedProducts);

      setPagination(
        data.pagination || {
          total: fetchedProducts.length,
          page,
          limit,
          totalPages: 1,
        },
      );

      /*
       * If the currently selected product exists
       * in the newly fetched list, update it as well.
       */
      setSelectedProduct((currentSelected) => {
        if (!currentSelected) return null;

        const updatedProduct = fetchedProducts.find(
          (product) => product._id === currentSelected._id,
        );

        return updatedProduct || null;
      });
    } catch (error) {
      console.error("Get all products error:", error);

      setProductError(error.message || "Unable to load products");
    } finally {
      setLoadingProducts(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    getAllProducts(1, 20);
  }, []);

  /*
  |--------------------------------------------------------------------------
  | PRODUCT CREATED
  |--------------------------------------------------------------------------
  |
  | AddProduct already creates the product through API.
  | After successful creation it can call this function if
  | you want the complete server list refreshed.
  |
  */

  const handleProductCreated = async () => {
    await getAllProducts(pagination.page, pagination.limit);
  };

  /*
  |--------------------------------------------------------------------------
  | GET SELECTED PRODUCT DETAILS
  |--------------------------------------------------------------------------
  */
  const [loadingProduct, setLoadingProduct] = useState(false);

  const handleProductSelect = async (product) => {
    const productId = product._id || product.id;

    if (!productId) return;
    setLoadingProduct(true);
    try {
      setProductError("");

      const response = await fetch(
        `${BASE_URL}api/user/products/${encodeURIComponent(productId)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch product details");
      }

      const productDetails = data.data?.product || data.data || data.product;

      if (!productDetails || typeof productDetails !== "object") {
        throw new Error("Product details were not returned by the server");
      }

      const selectedDetails = {
        ...productDetails,
        productName: productDetails.name,
        decimal: productDetails.allowDecimal ? "Yes" : "No",
        conversionQty:
          productDetails.unitConversion ?? productDetails.conversionQty ?? 1,
        tests: productDetails.tests || [],
      };

      setSelectedProduct(selectedDetails);
      setProducts((currentProducts) =>
        currentProducts.map((item) =>
          (item._id || item.id) === productId
            ? { ...item, ...selectedDetails }
            : item,
        ),
      );
    } catch (error) {
      console.error("Get product details error:", error);
      setProductError(error.message || "Unable to load product details");
    } finally {
      setLoadingProduct(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CARD STYLE
  |--------------------------------------------------------------------------
  */

  const cardStyle = {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
  };

  /*
  |--------------------------------------------------------------------------
  | LABEL STYLE
  |--------------------------------------------------------------------------
  */

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#334155",
  };

  /*
  |--------------------------------------------------------------------------
  | INPUT STYLE
  |--------------------------------------------------------------------------
  */

  const inputStyle = {
    height: "38px",
    borderRadius: "7px",
    fontSize: "13px",
  };

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* =========================================================
          MAIN LAYOUT
      ========================================================== */}

      <div
        className="products-main-layout"
        style={{
          display: "grid",

          /*
           * LEFT:
           * Add Product + Product Details
           *
           * RIGHT:
           * Product List
           */
          gridTemplateColumns: "minmax(0, 1fr) 260px",

          gap: "14px",

          width: "100%",

          alignItems: "start",
        }}
      >
        {/* =====================================================
            LEFT SIDE
        ====================================================== */}

        <div
          style={{
            minWidth: 0,
            width: "100%",
          }}
        >
          {/* ADD PRODUCT */}

          <AddProduct
            setProducts={setProducts}
            setSelectedProduct={setSelectedProduct}
            cardStyle={cardStyle}
            labelStyle={labelStyle}
            inputStyle={inputStyle}
            onProductCreated={handleProductCreated}
          />

          {/* PRODUCT DETAILS */}

          <ProductDetails
            cardStyle={cardStyle}
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
            products={products}
            setProducts={setProducts}
            loadingProduct={loadingProduct}
          />
        </div>

        {/* =====================================================
            RIGHT SIDE
        ====================================================== */}

        <ProductList
          cardStyle={cardStyle}
          products={products}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          onProductSelect={handleProductSelect}
          loading={loadingProducts}
          error={productError}
          pagination={pagination}
          onPageChange={(page) => getAllProducts(page, pagination.limit)}
          onSearch={(search) => getAllProducts(1, pagination.limit, search)}
        />
      </div>

      {/* =========================================================
          RESPONSIVE
      ========================================================== */}

      <style>
        {`
          @media (max-width: 991px) {
            .products-main-layout {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Products;
